// src/TeacherTermsView.js
//
// The authenticated Teacher Partner Terms screen — reached at the real path /teacher-terms
// (deep-link handled in App(), same mechanism as /teacher-dashboard) and from the Home banner /
// the Teacher Dashboard status line.
//
// This is the ONE place a teacher actually accepts the Terms. The public gated page
// (functions/affiliate/teacher-terms.html) is read-only and just links here. Both render the
// exact same body text from src/teacherTermsContent.js.
//
// Acceptance is recorded by the acceptTeacherPartnerTerms Cloud Function (write-once audit doc +
// email to support@speafy.com). While the [Date] placeholders are unresolved the module is in
// DRAFT mode and acceptance is disabled here and refused server-side.

import { useState, useEffect, Fragment, useCallback } from "react";
import { useT } from "./i18n";
import { isTeacher } from "./constants";
import { acceptTeacherPartnerTerms } from "./firebase";
import {
  TEACHER_TERMS_INTRO,
  TEACHER_TERMS_SECTIONS,
  TEACHER_TERMS_TOC,
  TEACHER_TERMS_ACCEPTANCE,
  TEACHER_TERMS_VERSION,
  TEACHER_TERMS_HASH,
  TEACHER_TERMS_DRAFT,
  TEACHER_TERMS_LAST_UPDATED,
} from "./teacherTermsContent";

// <inline> = string | { b } | Array<string | { b }>
function Inline({ value }) {
  const arr = Array.isArray(value) ? value : [value];
  return arr.map((seg, i) =>
    typeof seg === "string" ? <Fragment key={i}>{seg}</Fragment> : <strong key={i}>{seg.b}</strong>
  );
}

function Block({ block }) {
  if ("p" in block) return <p><Inline value={block.p} /></p>;
  if ("h3" in block) return <h3>{block.h3}</h3>;
  if ("ul" in block) {
    return <ul>{block.ul.map((it, i) => <li key={i}><Inline value={it} /></li>)}</ul>;
  }
  if ("ol" in block) {
    return <ol className="ttv-num">{block.ol.map((it, i) => <li key={i}><Inline value={it} /></li>)}</ol>;
  }
  if ("details" in block) {
    return (
      <details className="ttv-ex">
        <summary>{block.details.summary}</summary>
        <div className="ttv-ex-body">
          {block.details.blocks.map((b, i) => <Block key={i} block={b} />)}
        </div>
      </details>
    );
  }
  return null;
}

export default function TeacherTermsView({ profile, alreadyAccepted, onAccepted, onBack }) {
  const { t } = useT();
  const allowed = isTeacher(profile);
  useEffect(() => { if (!allowed) onBack(); }, [allowed, onBack]);

  const [checked, setChecked] = useState(false);
  const [state, setState] = useState("idle"); // idle | submitting | error
  const accepted = alreadyAccepted || null;

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  };

  // No fixed effective date — for this teacher the Terms take effect on the day they accept.
  // Before acceptance that's "today" (the day they're reading this); after, it's the recorded
  // acceptance date. Only shown in-app, never on the public page.
  const effectiveDate = fmtDate(accepted?.acceptedAt || new Date().toISOString());

  const submit = useCallback(async () => {
    if (!checked || state === "submitting" || TEACHER_TERMS_DRAFT) return;
    setState("submitting");
    try {
      const res = await acceptTeacherPartnerTerms({
        termsVersion: TEACHER_TERMS_VERSION,
        termsHash: TEACHER_TERMS_HASH,
      });
      onAccepted?.(res || { termsVersion: TEACHER_TERMS_VERSION, acceptedAt: new Date().toISOString() });
    } catch (e) {
      console.error("acceptTeacherPartnerTerms failed:", e);
      setState("error");
    }
  }, [checked, state, onAccepted]);

  if (!allowed) return null;

  const acceptanceBlock = accepted ? (
    <div className="ttv-accepted">
      <strong>{t("teacherTerms.acceptedOn", { date: fmtDate(accepted.acceptedAt) })}</strong>
      <br />
      {t("teacherTerms.acceptedVersion", { version: accepted.termsVersion || TEACHER_TERMS_VERSION })}
    </div>
  ) : TEACHER_TERMS_DRAFT ? (
    <div className="ttv-draft">{t("teacherTerms.draftNotice")}</div>
  ) : (
    <div className="ttv-accept">
      <div className="ttv-accept-row">
        <input
          type="checkbox"
          id="ttv-accept-checkbox"
          checked={checked}
          onChange={(e) => { setChecked(e.target.checked); if (state === "error") setState("idle"); }}
        />
        <label htmlFor="ttv-accept-checkbox">{TEACHER_TERMS_ACCEPTANCE.checkboxLabel}</label>
      </div>
      <button
        type="button"
        className="ttv-accept-btn"
        onClick={submit}
        disabled={!checked || state === "submitting"}
        aria-disabled={!checked || state === "submitting"}
      >
        {state === "submitting" ? t("teacherTerms.accepting") : TEACHER_TERMS_ACCEPTANCE.buttonLabel}
      </button>
      {state === "error" && <p className="ttv-accept-err">{t("teacherTerms.acceptError")}</p>}
    </div>
  );

  return (
    <div className="ttv teacher-dash">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("lesson.backBtn")}</button>
        <div className="lesson-title">{t("teacherTerms.screenTitle")}</div>
      </div>

      <p className="ttv-lead"><Inline value={TEACHER_TERMS_INTRO} /></p>
      <p className="ttv-dates">
        {t("teacherTerms.lastUpdatedLabel")}: {TEACHER_TERMS_LAST_UPDATED}
        {" · "}
        {t("teacherTerms.effectiveLabel")}: {effectiveDate}
      </p>

      <details className="ttv-toc">
        <summary>{t("teacherTerms.contents")}</summary>
        <ol>
          {TEACHER_TERMS_TOC.map((s) => (
            <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
          ))}
        </ol>
      </details>

      {TEACHER_TERMS_SECTIONS.map((section) => (
        <section className="ttv-section" key={section.n}>
          <h2 id={section.id || `section-${section.n}`}>{section.n}. {section.title}</h2>
          {section.blocks.map((b, i) => <Block key={i} block={b} />)}
          {section.n === 30 && acceptanceBlock}
        </section>
      ))}
    </div>
  );
}
